import { format, isValid, parseISO } from 'date-fns';
import { displayPersonaScore } from './personaSignalScore';

export const SIGNAL_TYPE_LABELS = {
  competitor_account_reference: 'Account named in competitor content',
  competitor_account_adoption: 'Competitor adoption at account',
  competitor_partner_ecosystem: 'Competitor partner ecosystem',
  competitor_procurement_signal: 'Procurement signal',
  competitor_event_overlap: 'Event overlap',
  competitor_displacement_opportunity: 'Displacement opportunity',
  competitor_weakness_signal: 'Competitor weakness',
  competitor_persona_interaction: 'Persona engaged by competitor',
  competitor_persona_reference: 'Persona named in competitor content',
  competitor_persona_adoption: 'Persona linked to competitor tool',
};

const PERSONA_COMPETITOR_PREFIX = 'competitor_persona_';

/** Account-barometer competitor signal types for Signals filter dropdown. */
export function accountCompetitorSignalTypeOptions() {
  return Object.entries(SIGNAL_TYPE_LABELS)
    .filter(([key]) => !key.startsWith(PERSONA_COMPETITOR_PREFIX))
    .map(([value, label]) => ({ value, label }));
}

/** Persona-barometer competitor signal types for Signals filter dropdown. */
export function personaCompetitorSignalTypeOptions() {
  return Object.entries(SIGNAL_TYPE_LABELS)
    .filter(([key]) => key.startsWith(PERSONA_COMPETITOR_PREFIX))
    .map(([value, label]) => ({ value, label }));
}

export function parseCompetitorSummary(s) {
  const fields = {};
  if (!s) return { competitor: null, strength: null, interpretation: null, evidence: null, recommended_action: null };
  for (const part of String(s).split('|').map((p) => p.trim())) {
    const [k, ...v] = part.split(':');
    if (k && v.length) fields[k.trim().toLowerCase()] = v.join(':').trim();
  }
  return {
    competitor: fields.competitor || null,
    strength: fields.strength || null,
    interpretation: fields.interpretation || null,
    evidence: fields.evidence || null,
    recommended_action: fields['recommended action'] || null,
  };
}

export function impactFromScoreChange(scoreChange) {
  const n = Number(scoreChange) || 0;
  const dir = n < 0 ? 'Unfavourable' : n > 0 ? 'Favourable' : 'Context only';
  const abs = Math.abs(n);
  const mag = abs >= 4 ? 'High' : abs >= 3 ? 'Medium' : abs >= 1 ? 'Low' : 'None';
  return { dir, mag };
}

export function signalTypeLabel(signalType) {
  if (!signalType) return 'Competitor signal';
  return SIGNAL_TYPE_LABELS[signalType] || 'Competitor signal';
}

export function competitorSignalSourceType(raw) {
  if (String(raw?.signal_type || '').startsWith('competitor_persona_')) return 'persona';
  if (raw?.momentum_score_change != null) return 'account';
  if (raw?.persona_signal_score != null) return 'persona';
  return 'account';
}

export function normalizeCompetitorSignal(raw) {
  return {
    id: raw.id,
    summary: raw.summary || raw.title || '',
    signal_type: raw.signal_type,
    date_published: raw.date_published || raw.weekly_period || raw.signal_date,
    score_change: raw.momentum_score_change
      ?? displayPersonaScore(raw)
      ?? raw.impact_score
      ?? 0,
    source_url: raw.news_source_url || raw.full_url || raw.source_url,
  };
}

export function formatSignalMonthYear(dateStr) {
  if (!dateStr) return '—';
  try {
    const d = typeof dateStr === 'string' ? parseISO(dateStr) : new Date(dateStr);
    if (!isValid(d)) return '—';
    return format(d, 'MM/yyyy');
  } catch {
    return '—';
  }
}

export function groupSignalsByCompetitor(signals) {
  const map = new Map();
  for (const raw of signals || []) {
    const sig = normalizeCompetitorSignal(raw);
    const parsed = parseCompetitorSummary(sig.summary);
    const name = parsed.competitor || 'Unknown';
    if (!map.has(name)) {
      map.set(name, {
        competitor: name,
        count: 0,
        strongest: null,
        latestDate: null,
        latestTs: 0,
      });
    }
    const g = map.get(name);
    g.count += 1;
    const sc = Number(sig.score_change) || 0;
    if (g.strongest === null || sc < g.strongest) g.strongest = sc;
    const ts = sig.date_published ? new Date(sig.date_published).getTime() : 0;
    if (ts > g.latestTs) {
      g.latestTs = ts;
      g.latestDate = sig.date_published;
    }
  }
  return Array.from(map.values()).sort((a, b) => b.count - a.count);
}

export function mergeCompetitorSignals(...lists) {
  const seen = new Set();
  const merged = [];
  for (const list of lists) {
    for (const raw of list || []) {
      const id = raw.id || `${raw.summary}-${raw.date_published}`;
      if (seen.has(id)) continue;
      seen.add(id);
      merged.push(raw);
    }
  }
  return merged.sort((a, b) => {
    const aTs = new Date(a.date_published || a.weekly_period || 0).getTime();
    const bTs = new Date(b.date_published || b.weekly_period || 0).getTime();
    return bTs - aTs;
  });
}
